import { useState, useEffect, useCallback } from "react";
import { useGetAllSchools } from "../../Dashboard";
import BackButton from "../../../../components/BackButton/BackButton";
import Table from "../../../../components/Table/Table";
import editIcon from "../../asserts/editiconschool.svg";
import Modal from "../../../../components/Modal/Modal";
import Input from "../../../../components/Input/Input";
import Select from "../../../../components/Select/Select";
import { useSearchParams } from "react-router-dom";
import { LoadingSpinner, Pagination } from "../School/pages/SchoolList";
import SearchFilterBar from "../School/components/SearchFilterBar";

type FilterOption = {
  value: string;
  name: string;
};

type FiltersState = {
  trustee: FilterOption | null;
  institute: FilterOption | null;
};

export enum FeeFrequency {
  MONTHLY = "MONTHLY",
  ANNUALLY = "ANNUALLY",
}

export enum ConvenienceFeeFrequency {
  ONE_TIME = "ONE_TIME",
  INSTALLMENT = "INSTALLMENT",
}

const updateConvenienceFee = async (
  convenience_fee: any,
  school_id: any,
  fee_frequency: any,
  convenience_fee_frequency: any
) => {
  const myHeaders = new Headers();
  myHeaders.append("Content-Type", "application/json");
  myHeaders.append("Authorization", "Bearer " + localStorage.getItem("token"));

  const raw = JSON.stringify({
    convenience_fee,
    fee_frequency,
    convenience_fee_frequency,
  });

  return await (
    await fetch(
      process.env.REACT_APP_BACKEND_URL +
        `/api/school/convenience-fee?school_id=${school_id}&convenience_fee=${convenience_fee}`,
      {
        method: "PATCH",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      }
    )
  ).json();
};

const ConvenienceFee = () => {
  const [school_id, set_school_id] = useState<any>("");
  const [open_modal, set_modal] = useState(false);
  const [convenience_fee, set_convenience_fee] = useState(0);

  const [fee_frequency, set_fee_frequency] = useState({
    name: FeeFrequency.MONTHLY,
  });

  const [convenience_fee_frequency, set_convenience_fee_frequency] = useState({
    name: ConvenienceFeeFrequency.INSTALLMENT,
  });

  const {
    data: schoolsData,
    loading: schoolsLoading,
    error: schoolsError,
    refetch: refetchSchoolsQuery,
  } = useGetAllSchools();

  const [searchParams, setSearchParams] = useSearchParams();

  const initialPage = parseInt(searchParams.get("page") || "1");
  const initialItemsPerRow = parseInt(searchParams.get("limit") || "10");
  const initialSearchText = searchParams.get("search") || "";
  const initialSearchFilter = searchParams.get("filter") || "school_id";

  const [searchText, setSearchText] = useState(initialSearchText);
  const [searchFilter, setSearchFilter] = useState(initialSearchFilter);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerRow, setItemsPerRow] = useState(initialItemsPerRow);
  const [refetchLoading, setRefetchLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localSchoolData, setLocalSchoolData] = useState<any>(null);

  const [filters, setFilters] = useState<FiltersState>({
    trustee: null,
    institute: null,
  });

  const buildRefetchParams = useCallback(
    (page: number) => {
      const params: any = {
        page,
        limit: itemsPerRow,
      };

      if (searchText.trim()) {
        if (searchFilter === "school_id") params.school_id = searchText.trim();
        if (searchFilter === "school_name")
          params.school_name = searchText.trim();
        if (searchFilter === "trustee_id")
          params.trustee_id = searchText.trim();
      }

      if (filters?.trustee && searchFilter !== "trustee_id") {
        params.trustee_id = filters.trustee.value;
      }

      if (filters?.institute && searchFilter !== "school_id") {
        params.school_id = filters.institute.value;
      }

      return params;
    },
    [itemsPerRow, searchText, searchFilter, filters]
  );

  useEffect(() => {
    if (schoolsData?.getallschool) {
      setLocalSchoolData(schoolsData.getallschool);
    }
  }, [schoolsData]);

  useEffect(() => {
    const params: any = {
      page: currentPage.toString(),
      limit: itemsPerRow.toString(),
    };

    if (searchText) {
      params.search = searchText;
      params.filter = searchFilter;
    }

    setSearchParams(params);
  }, [currentPage, itemsPerRow, searchText, searchFilter, setSearchParams]);

  const refetchSchools = async (params?: any) => {
    try {
      setRefetchLoading(true);
      const result = await refetchSchoolsQuery(params || {});
      if (result?.data?.getAllSchools) {
        setLocalSchoolData(result.data.getAllSchools);
      }
      return result;
    } finally {
      setRefetchLoading(false);
    }
  };

  const handlePageChange = async (page: number) => {
    setCurrentPage(page);
    setLoading(true);
    try {
      const params = buildRefetchParams(page);
      await refetchSchools(params);
    } finally {
      setLoading(false);
    }
  };

  async function handleSubmit() {
    const res = await updateConvenienceFee(
      convenience_fee,
      school_id,
      fee_frequency?.name,
      convenience_fee_frequency.name
    );

    if (res) {
      set_modal(false);
      setCurrentPage(1);
      setSearchText("");
      setFilters({ trustee: null, institute: null });

      const params = buildRefetchParams(1);
      await refetchSchools(params);
    }
  }

  const isLoading =
    schoolsLoading || loading || refetchLoading || !localSchoolData;

  if (schoolsError) {
    return (
      <div className="text-center py-8 text-red-500">
        Error loading school details
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-4">
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div>
            <div>
              <div className="flex items-center">
                <BackButton to={".."} />
                <div className="text-2xl font-semibold text-violet-500">
                  Convenience Fee
                </div>
              </div>
              <div className=" grid grid-cols-9">
                <div></div>
                <div></div>

                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>

              <Table
                loading={isLoading}
                searchBox={
                  <SearchFilterBar
                    refetchSchools={refetchSchools}
                    itemsPerRow={itemsPerRow}
                    setItemsPerRow={setItemsPerRow}
                    setCurrentPage={setCurrentPage}
                    searchText={searchText}
                    setSearchText={setSearchText}
                    searchFilter={searchFilter}
                    setSearchFilter={setSearchFilter}
                    filters={filters}
                    setFilters={setFilters}
                    setLoading={setLoading}
                    refetchLoading={refetchLoading}
                    setRefetchLoading={setRefetchLoading}
                    showContainerBorder={false}
                  />
                }
                footer={(() => {
                  const totalItems = localSchoolData.pagination?.total;
                  const totalPages = Math.ceil(totalItems / itemsPerRow);

                  return totalPages > 1 ? (
                    <div className="mt-6 flex justify-center items-center">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  ) : null;
                })()}
                data={[
                  [
                    "School Name",
                    "City",
                    "Chain",
                    "Students",
                    "Fee Frequency",
                    "Average Fee",
                    "Convenience Fee",
                    "Convenience Fee Frequency",
                    "Action",
                  ],
                  ...localSchoolData.data.map((s: any) => [
                    s.name,
                    s?.address?.city,
                    s?.chain,
                    s?.student_count,
                    s?.fee_frequency,
                    s?.average_fee,
                    s?.convenience_fee,
                    s?.convenience_fee_frequency,
                    <div className="flex justify-center cursor-pointer">
                      <img
                        onClick={() => {
                          set_modal(true);
                          set_school_id(s?._id);
                        }}
                        src={editIcon}
                        alt="edit"
                      />
                    </div>,
                  ]),
                ]}
              />

              <Modal
                title={"Set Convenience Fee"}
                open={open_modal}
                setOpen={set_modal}
              >
                <div>
                  <Input
                    onChange={(n: any, v: any) => set_convenience_fee(v)}
                    name={"Convenience Fee"}
                    add_error={() => {}}
                    type="number"
                    placeholder={"Enter Fee"}
                  />

                  <Select
                    options={[FeeFrequency.MONTHLY, FeeFrequency.ANNUALLY].map(
                      (c: any) => ({ name: c })
                    )}
                    setSelected={set_fee_frequency}
                    selected={fee_frequency}
                    label="Fee Frequency"
                  />

                  <Select
                    options={[
                      ConvenienceFeeFrequency.INSTALLMENT,
                      ConvenienceFeeFrequency.ONE_TIME,
                    ].map((c: any) => ({ name: c }))}
                    setSelected={set_convenience_fee_frequency}
                    selected={convenience_fee_frequency}
                    label="Convenience Fee Frequency"
                  />

                  <div className="flex justify-center">
                    <button
                      onClick={handleSubmit}
                      className="mt-2 py-2 px-4 bg-violet-500 rounded-md text-white font-semibold"
                    >
                      Submit
                    </button>
                  </div>
                </div>
              </Modal>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ConvenienceFee;
